import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BillingDetails.css';
import { useAuth } from '../../../contexts/AuthContext';

const PaymentHistory = () => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    billType: 'all',
    searchTerm: ''
  });
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      return 'mock-token-for-testing';
    }
    
    const lastFirebaseError = localStorage.getItem('firebaseAuthFailed');
    const errorTime = lastFirebaseError ? parseInt(lastFirebaseError) : 0;
    const now = Date.now();
    
    if (now - errorTime < 300000) {
      return 'mock-token-for-testing';
    }
    
    try {
      return await currentUser.getIdToken(true);
    } catch (error) {
      localStorage.setItem('firebaseAuthFailed', now.toString());
      return 'mock-token-for-testing';
    }
  }, [currentUser]);

  const fetchPaymentHistory = useCallback(async () => {
    if (!currentUser) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const token = await getAuthToken();
      
      const [cashBillsRes, creditBillsRes, creditNotesRes, debitNotesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/accounts/bill-details/cashBills`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/accounts/bill-details/creditBills`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/accounts/bill-details/creditNotes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/accounts/bill-details/debitNotes`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const normalizeData = (response) => {
        const data = response.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.value)) return data.value;
        if (Array.isArray(data?.data)) return data.data;
        return [];
      };

      const cashBills = normalizeData(cashBillsRes);
      const creditBills = normalizeData(creditBillsRes);
      const creditNotes = normalizeData(creditNotesRes);
      const debitNotes = normalizeData(debitNotesRes);

      const allPayments = [
        ...cashBills.map(bill => ({
          id: bill._id || bill.id,
          invoiceNumber: bill.invoiceNumber || bill.invoiceNo || 'N/A',
          billType: 'Cash Bill',
          customerName: bill.customerName || bill.customer || 'N/A',
          amount: bill.grandTotal || bill.totalAmount || 0,
          paymentDate: bill.date || bill.createdAt || new Date().toISOString(),
          paymentMethod: 'Cash',
          status: 'Completed',
          processedBy: bill.createdBy || 'System'
        })),
        ...creditBills.map(bill => ({
          id: bill._id || bill.id,
          invoiceNumber: bill.invoiceNumber || bill.invoiceNo || 'N/A',
          billType: 'Credit Bill',
          customerName: bill.customerName || bill.customer || 'N/A',
          amount: bill.grandTotal || bill.totalAmount || 0,
          paymentDate: bill.date || bill.createdAt || new Date().toISOString(),
          paymentMethod: 'Credit',
          status: 'Completed',
          processedBy: bill.createdBy || 'System'
        })),
        ...creditNotes.map(note => ({
          id: note._id || note.id,
          invoiceNumber: note.invoiceNumber || note.invoiceNo || 'N/A',
          billType: 'Credit Note',
          customerName: note.customerName || note.customer || 'N/A',
          amount: note.Total || note.totalAmount || 0,
          paymentDate: note.date || note.createdAt || new Date().toISOString(),
          paymentMethod: 'Credit Note',
          status: 'Completed',
          processedBy: note.createdBy || 'System'
        })),
        ...debitNotes.map(note => ({
          id: note._id || note.id,
          invoiceNumber: note.invoiceNumber || note.invoiceNo || 'N/A',
          billType: 'Debit Note',
          customerName: note.customerName || note.customer || 'N/A',
          amount: note.totalAmount || note.Total || 0,
          paymentDate: note.date || note.createdAt || new Date().toISOString(),
          paymentMethod: 'Debit Note',
          status: 'Completed',
          processedBy: note.createdBy || 'System'
        }))
      ];

      setPaymentHistory(allPayments);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch payment history');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, getAuthToken, API_BASE_URL]);

  useEffect(() => {
    fetchPaymentHistory();
  }, [fetchPaymentHistory]);

  const filteredPayments = paymentHistory.filter(payment => {
    const paymentDate = new Date(payment.paymentDate || payment.date);
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;
    
    if (startDate && paymentDate < startDate) return false;
    if (endDate && paymentDate > endDate) return false;
    
    if (filters.billType !== 'all' && payment.billType !== filters.billType) return false;
    
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      return (
        payment.invoiceNumber?.toLowerCase().includes(searchLower) ||
        payment.customerName?.toLowerCase().includes(searchLower) ||
        payment.paymentMethod?.toLowerCase().includes(searchLower) ||
        payment.billType?.toLowerCase().includes(searchLower) ||
        payment.id?.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getBillTypeColor = (billType) => {
    switch (billType) {
      case 'cashbill': return '#3498db';
      case 'creditbill': return '#9b59b6';
      case 'creditnote': return '#e74c3c';
      case 'debitnote': return '#f1c40f';
      default: return '#95a5a6';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="billing-details">
        <div className="loading-overlay">Loading payment history...</div>
      </div>
    );
  }

  return (
    <div className="billing-details">
      <div className="payment-history-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Go Back"
          >
            ← Back
          </button>
          <h2>Payment History</h2>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="filters-section">
        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
          />
        </div>
        
        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
          />
        </div>
        
        <div className="filter-group">
          <label>Bill Type:</label>
          <select name="billType" value={filters.billType} onChange={handleFilterChange}>
            <option value="all">All Types</option>
            <option value="Cash Bill">Cash Bill</option>
            <option value="Credit Bill">Credit Bill</option>
            <option value="Credit Note">Credit Note</option>
            <option value="Debit Note">Debit Note</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            name="searchTerm"
            value={filters.searchTerm}
            onChange={handleFilterChange}
            placeholder="Search by invoice number, customer..."
          />
        </div>
        
        <div className="filter-actions">
          <button 
            className="clear-filters-btn"
            onClick={() => setFilters({
              startDate: '',
              endDate: '',
              billType: 'all',
              searchTerm: ''
            })}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="summary-stats">
        <div className="stat-card">
          <h3>Total Payments</h3>
          <p>{filteredPayments.length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Amount</h3>
          <p>{formatCurrency(filteredPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0))}</p>
        </div>
        <div className="stat-card">
          <h3>Average Payment</h3>
          <p>{filteredPayments.length > 0 ? formatCurrency(filteredPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0) / filteredPayments.length) : '₹0.00'}</p>
        </div>
      </div>

      <div className="payment-history-table">
        <h3>Payment Details</h3>
        {filteredPayments.length === 0 ? (
          <p>No payment history found.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice Number</th>
                <th>Bill Type</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Processed By</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment, index) => (
                <tr key={payment._id || index}>
                  <td>{new Date(payment.paymentDate || payment.date).toLocaleDateString()}</td>
                  <td>{payment.invoiceNumber || 'N/A'}</td>
                  <td>
                    <span 
                      className="bill-type-badge"
                      style={{ backgroundColor: getBillTypeColor(payment.billType) }}
                    >
                      {payment.billType?.toUpperCase() || 'N/A'}
                    </span>
                  </td>
                  <td>{payment.customerName || 'N/A'}</td>
                  <td className="amount-cell">{formatCurrency(payment.amount || 0)}</td>
                  <td>{payment.paymentMethod || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${payment.status?.toLowerCase()}`}>
                      {payment.status || 'Completed'}
                    </span>
                  </td>
                  <td>{payment.processedBy || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
